import { ThemeProvider } from '@shopify/restyle'
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

import { defaultDarkTheme, defaultLightTheme } from './toRestyleTheme'
import {
  type ColorScheme,
  resolveColorScheme,
  useColorScheme,
} from './useColorScheme'
import { type FunkitThemeSet, selectTheme } from './useFunkitTheme'

const DEFAULT_THEMES: FunkitThemeSet = {
  light: defaultLightTheme,
  dark: defaultDarkTheme,
}

interface FunkitColorSchemeValue {
  /** The active, resolved color scheme. */
  colorScheme: ColorScheme
  /**
   * Flip light↔dark, or set a specific scheme when an argument is given.
   * **No-op when the provider's `colorScheme` prop is set** (controlled mode) —
   * the host owns the scheme in that case.
   */
  toggleColorScheme: (next?: ColorScheme) => void
  /** True when the scheme is forced by the `colorScheme` prop (uncontrolled toggle disabled). */
  isControlled: boolean
}

const FunkitColorSchemeContext = createContext<FunkitColorSchemeValue | null>(
  null,
)

export interface FunkitThemeProviderProps {
  children: ReactNode
  /**
   * The light/dark Restyle theme set. Defaults to the built-in Fun Mobile
   * themes. Pass `{ light, dark }` from `toRestyleTheme(...)` to customize.
   */
  themes?: FunkitThemeSet
  /**
   * Force a color scheme. When provided the provider is **controlled**: it
   * always renders this scheme, ignores the system Appearance, and
   * `toggleColorScheme()` becomes a no-op. When omitted the provider is
   * **uncontrolled**: the scheme follows the system Appearance and can be
   * flipped with `toggleColorScheme()`.
   */
  colorScheme?: ColorScheme
}

/**
 * The styling layer for `@funkit/connect-rn`. Resolves the active Restyle theme
 * (selected from `themes` by color scheme), wires it into Restyle's
 * `ThemeProvider`, and exposes the scheme + a manual toggle via
 * {@link useFunkitColorScheme}.
 *
 * This is an internal-ish provider — host apps mount the user-facing
 * {@link FunkitProvider}, which passes `themes` / `colorScheme` down to this.
 */
export function FunkitThemeProvider({
  children,
  themes = DEFAULT_THEMES,
  colorScheme,
}: FunkitThemeProviderProps) {
  const isControlled = colorScheme != null
  const systemScheme = useColorScheme()
  // Manual override, used only in uncontrolled mode. Sticky once set — later
  // system changes are ignored until the next toggle (matches web behavior).
  const [override, setOverride] = useState<ColorScheme | undefined>(undefined)

  const activeScheme = resolveColorScheme(colorScheme, override, systemScheme)

  const toggleColorScheme = useCallback(
    (next?: ColorScheme) => {
      if (colorScheme != null) {
        return // controlled: the host owns the scheme
      }
      setOverride((prev) => {
        if (next != null) {
          return next
        }
        const current = resolveColorScheme(undefined, prev, systemScheme)
        return current === 'dark' ? 'light' : 'dark'
      })
    },
    [colorScheme, systemScheme],
  )

  const theme = selectTheme(themes, activeScheme)

  const value = useMemo<FunkitColorSchemeValue>(
    () => ({ colorScheme: activeScheme, toggleColorScheme, isControlled }),
    [activeScheme, toggleColorScheme, isControlled],
  )

  return (
    <FunkitColorSchemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </FunkitColorSchemeContext.Provider>
  )
}

/**
 * Read the active color scheme and toggle it. Must be used within a
 * {@link FunkitProvider} (which mounts {@link FunkitThemeProvider}).
 * `toggleColorScheme` is a no-op when the provider is in controlled mode (a
 * `colorScheme` prop was passed).
 */
export function useFunkitColorScheme(): FunkitColorSchemeValue {
  const value = useContext(FunkitColorSchemeContext)
  if (value == null) {
    throw new Error('useFunkitColorScheme must be used within <FunkitProvider>')
  }
  return value
}
